import React, { useEffect, useState } from "react";
import { Save, Upload, X, MapPin, Image as ImageIcon, HelpCircle, Plus, Trash2, Edit2, ChevronUp, ChevronDown, Check, ListPlus, MessageCircle, RotateCcw, Send, Mail } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { shrinkPhotos, splitOversized, readJson, PHOTO_MAX_MB } from "../utils/uploadPhotos";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const BE  = process.env.REACT_APP_BACKEND_URL || "";

function imgSrc(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BE}${url}`;
}

const inputClass = "w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm " +
  "focus:outline-none focus:ring-2 focus:ring-amber-400/60 focus:border-amber-400 transition-all";

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-stone-400 mt-1">{hint}</p>}
    </div>
  );
}

function SaveButton({ onClick, saving, label = "Save" }) {
  return (
    <button onClick={onClick} disabled={saving}
      className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-navy-900 text-white text-sm font-semibold
        hover:bg-navy-800 disabled:opacity-60 transition-all">
      <Save size={14} /> {saving ? "Saving…" : label}
    </button>
  );
}

export default function SettingsPage() {
  // `features` gates the in-room ordering toggle — POS is a platform-level
  // flag, so a property without it sees the row explained rather than a
  // switch that silently does nothing.
  const { authHeaders, features } = useAuth();
  const settings = useSettings();
  const [banner, setBanner] = useState("");
  // WhatsApp/Telegram/Email/Razorpay credential status — moved here from
  // the super-admin console (2026-09-07); each tenant now configures their
  // own. Fetched once and handed down, rather than four separate fetches
  // for four sections that all need the same masked-status document.
  const [integrations, setIntegrations] = useState(null);
  const loadIntegrations = () => fetch(`${API}/integrations`, { headers: authHeaders() })
    .then(r => r.json()).then(j => { if (j.success) setIntegrations(j.data); }).catch(() => {});
  useEffect(() => { loadIntegrations(); }, []);

  // Remembered per viewer, same reason the calendar's month/list choice is:
  // bouncing back to the first tab on every visit is a small, constant
  // annoyance for whichever section someone actually works in.
  const [tab, setTabRaw] = useState(() => {
    try { return localStorage.getItem("hostsync_settings_tab") || ""; }
    catch { return ""; }
  });
  const selectTab = (name) => {
    setTabRaw(name);
    try { localStorage.setItem("hostsync_settings_tab", name); } catch {}
  };

  function flash(msg) { setBanner(msg); setTimeout(() => setBanner(""), 2500); }

  if (!settings.loaded || !integrations) return <p className="p-4 sm:p-6 text-sm text-stone-400">Loading…</p>;

  const saved = (msg) => () => { settings.refresh(); flash(msg); };
  const savedIntegration = (msg) => () => { loadIntegrations(); flash(msg); };

  // Twelve sections on one endless scroll had become genuinely hard to
  // navigate — grouped into four tabs instead, mirroring how the
  // super-admin console's own tenant detail page is already organised.
  const TABS = {
    Property: (
      <>
        <ProfileSection authHeaders={authHeaders} settings={settings} onSaved={saved("Saved.")} />
        <ThemeSection authHeaders={authHeaders} settings={settings} onSaved={saved("Theme updated.")} />
        <LogoSection authHeaders={authHeaders} settings={settings} onSaved={saved("Logo updated.")} />
        <PhotosSection authHeaders={authHeaders} settings={settings} onSaved={saved("Photos updated.")} />
      </>
    ),
    "Booking Page": (
      <>
        <BookingFormSection authHeaders={authHeaders} settings={settings} onSaved={saved("Saved.")} />
        <GuestOrderingSection authHeaders={authHeaders} settings={settings} posEnabled={!!features?.pos}
          onSaved={saved("Saved.")} />
        <CustomFieldsSection authHeaders={authHeaders} onSaved={saved("Field updated.")} />
        <FaqSection authHeaders={authHeaders} onSaved={saved("FAQ updated.")} />
      </>
    ),
    Notifications: (
      <>
        <WhatsAppIntegrationSection authHeaders={authHeaders} integrations={integrations} onSaved={savedIntegration("WhatsApp settings saved.")} />
        <WhatsAppTemplateMappingSection authHeaders={authHeaders} integrations={integrations} onSaved={savedIntegration("Template mapping saved.")} />
        <TelegramIntegrationSection authHeaders={authHeaders} integrations={integrations} onSaved={savedIntegration("Telegram settings saved.")} />
        <EmailIntegrationSection authHeaders={authHeaders} integrations={integrations} onSaved={savedIntegration("Email settings saved.")} />
        <MessageTemplatesSection authHeaders={authHeaders} onSaved={() => flash("Message wording saved.")} />
        <NotificationTestSection authHeaders={authHeaders} />
      </>
    ),
    Payments: (
      <PaymentsSection authHeaders={authHeaders} settings={settings} integrations={integrations}
        onSaved={() => { settings.refresh(); loadIntegrations(); flash("Saved."); }} />
    ),
  };
  const tabNames = Object.keys(TABS);
  const activeTab = tabNames.includes(tab) ? tab : tabNames[0];

  return (
    // Same page frame every other admin page uses (Dashboard, Promotions,
    // Reviews, Logs, …): padded, centred, max-width capped — without this
    // the content sits flush against the sidebar.
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-navy-900">Settings</h1>
        <p className="text-sm text-stone-400 mt-1">
          This is what guests see on your public booking page — keep it current.
        </p>
      </div>

      {/* Horizontally scrollable so all four stay reachable at 360px
          (CLAUDE.md I7) rather than wrapping into a cramped second row. */}
      <div className="flex gap-1 overflow-x-auto -mx-1 px-1 border-b border-stone-200">
        {tabNames.map(name => (
          <button key={name} onClick={() => selectTab(name)}
            className={`px-4 py-2.5 min-h-[44px] text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
              activeTab === name
                ? "border-amber-500 text-amber-600"
                : "border-transparent text-stone-500 hover:text-stone-800"
            }`}>
            {name}
          </button>
        ))}
      </div>

      {banner && <div className="bg-emerald-50 text-emerald-700 text-sm rounded-xl px-4 py-2.5">{banner}</div>}

      <div className="space-y-10 pt-2">{TABS[activeTab]}</div>
    </div>
  );
}

/* ── Send a real test message on each configured channel — moved here from
   the Bookings page toolbar, where it had nothing to do with bookings and
   crowded a page whose job is managing reservations. Same three routes
   (`/whatsapp/test`, `/telegram/test`, `/email/test`) called exactly the
   way the old toolbar modals did; just relocated, not rebuilt. ── */
function NotificationTestSection({ authHeaders }) {
  return (
    <div className="space-y-4">
      <h2 className="text-sm font-semibold text-navy-900 uppercase tracking-wider">Test Notifications</h2>
      <p className="text-xs text-stone-400 -mt-2">
        Send a real test message through each channel you've configured, to confirm it actually
        works before relying on it for guests.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <TestChannelCard
          icon={<MessageCircle size={16} className="text-green-600" />}
          title="WhatsApp"
          endpoint="/whatsapp/test"
          authHeaders={authHeaders}
          fields={[{ key: "to", label: "Phone Number", type: "tel", placeholder: "9876543210", required: true }]}
          mockNote="Sent in mock mode — no WhatsApp credentials configured yet, so this was only logged on the server console, not actually delivered."
          okNote="Accepted by WhatsApp — check the phone to confirm it arrived. With Meta, the test uses the pre-approved hello_world template, which arrives even if that number never messaged you; if it still doesn't arrive, the token, number or recipient is the problem."
          buttonClass="from-green-500 to-green-600"
        />
        <TestChannelCard
          icon={<Send size={16} className="text-blue-500" />}
          title="Telegram"
          endpoint="/telegram/test"
          authHeaders={authHeaders}
          fields={[]}
          mockNote="Sent in mock mode — no Telegram bot token/chat ID configured yet, so this was only logged on the server console, not actually delivered."
          okNote="Delivered via the Telegram bot."
          buttonClass="from-blue-500 to-blue-600"
        />
        <TestChannelCard
          icon={<Mail size={16} className="text-amber-600" />}
          title="Email"
          endpoint="/email/test"
          authHeaders={authHeaders}
          fields={[{ key: "to", label: "Email Address", type: "email", placeholder: "you@example.com", required: true }]}
          mockNote="Sent in mock mode — no SMTP relay configured yet for this tenant, so this was only logged on the server console, not actually delivered."
          okNote="Delivered via this tenant's own SMTP relay."
          buttonClass="from-amber-500 to-orange-500"
        />
      </div>
    </div>
  );
}

function TestChannelCard({ icon, title, endpoint, authHeaders, fields, mockNote, okNote, buttonClass }) {
  const [values, setValues] = useState(Object.fromEntries(fields.map(f => [f.key, ""])));
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // { ok, mock, error }

  const missingRequired = fields.some(f => f.required && !values[f.key]?.trim());

  async function send() {
    setSending(true);
    setResult(null);
    try {
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ ...values, message: message.trim() || undefined }),
      });
      const json = await res.json();
      if (json.success) setResult({ ok: true, mock: json.data.mock });
      else setResult({ ok: false, error: json.error });
    } catch (e) {
      setResult({ ok: false, error: e.message || "Request failed" });
    }
    setSending(false);
  }

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4 space-y-3">
      <p className="font-bold text-navy-900 flex items-center gap-2 text-sm">{icon} {title}</p>
      {fields.map(f => (
        <div key={f.key}>
          <label className="text-xs font-medium text-stone-500 mb-1 block">{f.label}</label>
          <input
            type={f.type}
            value={values[f.key]}
            onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}
            placeholder={f.placeholder}
            className={inputClass}
          />
        </div>
      ))}
      <div>
        <label className="text-xs font-medium text-stone-500 mb-1 block">Message (optional)</label>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          rows={2}
          placeholder="Leave blank for a default test message"
          className={inputClass + " resize-none"}
        />
      </div>
      {result && (
        result.ok ? (
          <div className="bg-green-50 border border-green-200 rounded-xl px-3 py-2 text-xs text-green-700">
            {result.mock ? mockNote : `✓ ${okNote}`}
          </div>
        ) : (
          <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700">
            {result.error}
          </div>
        )
      )}
      <button
        onClick={send}
        disabled={sending || missingRequired}
        className={`w-full py-2.5 rounded-xl bg-gradient-to-r ${buttonClass} text-white text-sm font-medium disabled:opacity-60`}
      >
        {sending ? "Sending…" : "Send Test"}
      </button>
    </div>
  );
}

/* ── Colour theme — one of a few curated palettes, not a hex picker (see
   backend/config/themePresets.js for why). Applies to the whole app and to
   the invoice PDF, since both resolve the same preset server-side. ── */
function ThemeSection({ authHeaders, settings, onSaved }) {
  const [themes, setThemes] = useState([]);
  const [selected, setSelected] = useState(settings.themeId || "amber");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API}/themes`).then(r => r.json())
      .then(j => { if (j.success) setThemes(j.data); }).catch(() => {});
  }, []);

  async function choose(id) {
    const previous = selected;
    setSelected(id);            // optimistic: the swatch reacts instantly
    setSaving(true); setError("");
    try {
      const res = await fetch(`${API}/settings/profile`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ themeId: id }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Save failed");
      onSaved();                // refreshes settings → repaints the whole app
    } catch (e) { setError(e.message); setSelected(previous); }
    finally { setSaving(false); }
  }

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-navy-900 uppercase tracking-wider">Colour Theme</h2>
      <p className="text-xs text-stone-400">
        Applies across your dashboard, your public booking page and your guest invoices.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {themes.map(t => (
          <button key={t.id} type="button" onClick={() => choose(t.id)} disabled={saving}
            aria-pressed={selected === t.id}
            className={`text-left rounded-2xl border p-3 transition-all disabled:opacity-60 ${
              selected === t.id
                ? "border-amber-500 ring-2 ring-amber-400/40 bg-amber-50/40"
                : "border-stone-200 hover:border-stone-300"
            }`}>
            <div className="flex items-center gap-1.5 mb-2">
              <span className="w-6 h-6 rounded-full border border-black/5" style={{ background: t.swatch.primary }} />
              <span className="w-6 h-6 rounded-full border border-black/5" style={{ background: t.swatch.accent }} />
              <span className="w-6 h-6 rounded-full border border-black/5" style={{ background: t.swatch.tint }} />
              {selected === t.id && <Check size={15} className="text-amber-600 ml-auto" />}
            </div>
            <p className="text-sm font-medium text-navy-900">{t.name}</p>
            <p className="text-[11px] text-stone-400 leading-snug mt-0.5">{t.description}</p>
          </button>
        ))}
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

// Shared by the three integration sections below — one PATCH /integrations
// call, only the relevant top-level key populated.
async function saveIntegration(authHeaders, body, onSaved, onError) {
  try {
    const res = await fetch(`${API}/integrations`, {
      method: "PATCH",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.error || "Save failed");
    onSaved();
  } catch (e) { onError(e.message); }
}

/* ── WhatsApp — moved here from the super-admin console (2026-09-07); each
   tenant now enters their own MSG91/Meta credentials directly.

   The delivery settings below are what make messages actually ARRIVE.
   WhatsApp only delivers a business-initiated message as an approved
   template unless the guest messaged the business in the last 24 hours;
   plain text outside that window is accepted by the API and then dropped,
   which is how a test could report success while nothing reached the phone. ── */
function WhatsAppIntegrationSection({ authHeaders, integrations, onSaved }) {
  const wa = integrations.whatsapp;
  const [provider, setProvider] = useState(wa.provider || "msg91");
  const [apiKey, setApiKey] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [useTemplates, setUseTemplates] = useState(!!wa.useTemplates);
  const [templateLanguage, setTemplateLanguage] = useState(wa.templateLanguage || "en");
  const [adminNumber, setAdminNumber] = useState(wa.adminNumber || "");
  // Superseded by Template Mapping below; kept so saving here re-saves it unchanged.
  const invoiceTemplate = wa.invoiceTemplate || "";
  const [appSecret, setAppSecret] = useState("");
  const [verifyToken, setVerifyToken] = useState(wa.webhookVerifyToken || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isMeta = provider === "meta";
  const webhookUrl = `${window.location.origin}/api/whatsapp/webhook`;

  function generateToken() {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    setVerifyToken(Array.from(bytes, b => b.toString(16).padStart(2, "0")).join(""));
  }

  async function save() {
    setSaving(true); setError("");
    const body = { whatsapp: {
      provider,
      ...(apiKey && { apiKey }),
      ...(phoneNumberId && { phoneNumberId }),
      useTemplates, templateLanguage, adminNumber, invoiceTemplate,
      webhookVerifyToken: verifyToken,
      ...(appSecret && { appSecret }),
    } };
    await saveIntegration(authHeaders, body, () => { setApiKey(""); setPhoneNumberId(""); setAppSecret(""); onSaved(); }, setError);
    setSaving(false);
  }

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-navy-900 uppercase tracking-wider flex items-center gap-2">
        <MessageCircle size={15} className="text-green-600" /> WhatsApp
        {wa.apiKeySet && <span className="text-emerald-600 normal-case font-normal text-xs">· configured</span>}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Provider">
          <select value={provider} onChange={e => setProvider(e.target.value)} className={inputClass}>
            <option value="msg91">MSG91 (Meta Business Solution Provider)</option>
            <option value="meta">Meta Direct (WhatsApp Cloud API)</option>
          </select>
        </Field>
        <Field label={isMeta ? "Access Token" : "Authkey"}>
          <input value={apiKey} onChange={e => setApiKey(e.target.value)} className={inputClass}
            placeholder={wa.apiKeySet ? "•••••••• (leave blank to keep)" : (isMeta ? "Meta permanent access token" : "MSG91 authkey")} />
        </Field>
      </div>
      <Field label="Phone Number ID">
        <input value={phoneNumberId} onChange={e => setPhoneNumberId(e.target.value)} className={inputClass}
          placeholder={wa.phoneNumberId || (isMeta ? "1234567890 (Meta phone number ID)" : "919876543210")} />
      </Field>

      <div className="rounded-xl border border-stone-200 bg-stone-50/60 p-4 space-y-4">
        <p className="text-xs font-semibold text-navy-900 uppercase tracking-wider">Delivery</p>

        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={useTemplates} onChange={e => setUseTemplates(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-amber-500 flex-shrink-0" />
          <span>
            <span className="block text-sm font-medium text-navy-900">Send guest messages as approved templates</span>
            <span className="block text-xs text-stone-400 mt-0.5">
              Required for messages to reach guests reliably. Without it, WhatsApp only delivers to
              guests who messaged your business number in the last 24 hours — everything else is
              accepted and silently dropped. Turn on once your templates are approved.
            </span>
          </span>
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Template language code">
            <input value={templateLanguage} onChange={e => setTemplateLanguage(e.target.value)} className={inputClass}
              placeholder="en or en_US" />
          </Field>
          <Field label="Admin alert number">
            <input value={adminNumber} onChange={e => setAdminNumber(e.target.value)} className={inputClass}
              placeholder="919876543210 — new booking alerts" />
          </Field>
        </div>
        <p className="text-[11px] text-stone-400 -mt-2">
          The language must match exactly what your templates were approved under — "en" and "en_US" are
          different to WhatsApp, and a mismatch makes every template fail.
        </p>

        {isMeta && (
          <>
            <p className="text-[11px] text-stone-400">
              To use templates you've already had approved — including one for invoice PDFs — map them in
              <strong> WhatsApp Template Mapping</strong> below.
            </p>

            <div className="border-t border-stone-200 pt-4 space-y-3">
              <p className="text-xs font-semibold text-navy-900">Delivery reports (Meta webhook)</p>
              <p className="text-xs text-stone-400">
                Lets the Logs page show whether each message was actually <strong>delivered</strong>, read, or
                failed — and why — instead of only that it was sent. In your Meta app → WhatsApp →
                Configuration, set the callback URL and verify token below, then subscribe to <span className="font-mono">messages</span>.
              </p>
              <Field label="Callback URL">
                <input readOnly value={webhookUrl} className={`${inputClass} font-mono text-xs bg-white`} onFocus={e => e.target.select()} />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Verify token">
                  <div className="flex gap-2">
                    <input value={verifyToken} onChange={e => setVerifyToken(e.target.value)} className={`${inputClass} font-mono text-xs`} placeholder="any random string" />
                    <button type="button" onClick={generateToken}
                      className="px-3 min-h-[44px] rounded-xl border border-stone-200 text-xs text-stone-600 hover:bg-white flex-shrink-0">
                      Generate
                    </button>
                  </div>
                </Field>
                <Field label="App Secret">
                  <input type="password" value={appSecret} onChange={e => setAppSecret(e.target.value)} className={inputClass}
                    placeholder={wa.appSecretSet ? "•••••••• (leave blank to keep)" : "Meta app → Settings → Basic"} />
                </Field>
              </div>
            </div>
          </>
        )}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      <SaveButton onClick={save} saving={saving} />
    </div>
  );
}

/* ── WhatsApp template mapping ──
   Lets a tenant use templates they ALREADY had approved in Meta instead of
   creating new ones named and shaped the way our defaults expect. For each
   message they pick a template and match each of its {{variables}} to a
   value we fill in. Anything left unmapped keeps using the default template
   name and variable order, so nothing changes until a tenant maps something.
   Only takes effect in template mode. ── */
function WhatsAppTemplateMappingSection({ authHeaders, integrations, onSaved }) {
  const wa = integrations.whatsapp;
  const isMeta = wa.provider === "meta";
  // What a row with no language of its own is sent with — shown in that box
  // instead of a fixed example, which tenants read as a locked value.
  const defaultLanguage = wa.templateLanguage || "en";
  const [meta, setMeta] = useState(null);        // { events, tokenLabels } from the server
  const [mapping, setMapping] = useState(() => {
    const m = { ...(wa.templates || {}) };
    // Carry a legacy single invoice-template setting into the Invoice row.
    if (!m.invoice && wa.invoiceTemplate) m.invoice = { name: wa.invoiceTemplate, params: ["ref"] };
    return m;
  });
  const [wabaId, setWabaId] = useState(wa.wabaId || "");
  const [approved, setApproved] = useState(null); // templates read from Meta
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [openKey, setOpenKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API}/integrations/whatsapp/events`, { headers: authHeaders() })
      .then(r => r.json()).then(j => { if (j.success) setMeta(j.data); }).catch(() => {});
  }, []);

  async function loadTemplates() {
    setLoading(true); setLoadError("");
    try {
      const qs = wabaId.trim() ? `?wabaId=${encodeURIComponent(wabaId.trim())}` : "";
      const res = await fetch(`${API}/integrations/whatsapp/templates${qs}`, { headers: authHeaders() });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || "Couldn't load templates.");
      setApproved(j.data.templates);
    } catch (e) { setLoadError(e.message); }
    finally { setLoading(false); }
  }

  function setEvent(key, value) {
    setMapping(m => {
      const next = { ...m };
      if (value) next[key] = value; else delete next[key];
      return next;
    });
  }

  function pickTemplate(key, def, tpl) {
    if (!tpl) { setEvent(key, null); return; }
    // Seed each variable with our default value for that position, so a
    // template shaped like our default maps itself with no clicks.
    const params = tpl.variables.map((_, i) => def.defaultParams[i] || def.tokens[0]);
    setEvent(key, { name: tpl.name, language: tpl.language, params, ...(tpl.named ? { paramNames: tpl.variables } : {}) });
  }

  async function save() {
    setSaving(true); setError("");
    await saveIntegration(authHeaders, { whatsapp: { templates: mapping, wabaId: wabaId.trim() } }, onSaved, setError);
    setSaving(false);
  }

  if (!meta) return null;
  const events = Object.entries(meta.events);
  const mappedCount = events.filter(([k]) => mapping[k]?.name).length;
  const approvedCount = (approved || []).filter(t => t.status === "APPROVED").length;

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-navy-900 uppercase tracking-wider flex items-center gap-1.5">
        <MessageCircle size={14} /> WhatsApp Template Mapping
      </h2>
      <p className="text-xs text-stone-400">
        Already have approved templates in Meta? Point each message at one and match its variables to what we fill in.
        Anything left unmapped uses our default template name —{" "}
        {mappedCount === 0 ? "nothing mapped yet" : `${mappedCount} of ${events.length} mapped`}.
      </p>
      {!wa.useTemplates && (
        <p className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
          Template mode is off, so these mappings aren't used yet. Turn on “Send guest messages as approved templates” above.
        </p>
      )}

      {isMeta ? (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <button type="button" onClick={loadTemplates} disabled={loading || !wa.apiKeySet}
              className="min-h-[44px] px-4 rounded-xl border border-stone-200 bg-white text-sm font-medium text-navy-900 hover:bg-stone-50 disabled:opacity-50">
              {loading ? "Loading…" : approved ? "Reload templates from Meta" : "Load my approved templates from Meta"}
            </button>
            <div className="flex-1 min-w-[200px]">
              <Field label="WhatsApp Business Account ID (only if loading fails)">
                <input value={wabaId} onChange={e => setWabaId(e.target.value)} className={inputClass} placeholder="e.g. 102290129340398" />
              </Field>
            </div>
          </div>
          <p className="text-[11px] text-stone-400 -mt-1">
            Find it in WhatsApp Manager → Account tools → Account overview, as
            “WhatsApp Business Account ID”. Meta shows several similar-looking IDs — the app ID,
            the phone number ID and the Business portfolio ID are all different things. A Business
            portfolio ID works here too; we'll look inside it for the WhatsApp account.
          </p>
        </>
      ) : (
        <p className="text-[11px] text-stone-400">With MSG91, type each template's exact name and add its variables in order.</p>
      )}
      {loadError && <p className="text-xs text-red-600">{loadError}</p>}
      {approved && (
        <p className="text-[11px] text-stone-400">
          Found {approved.length} template{approved.length === 1 ? "" : "s"} in Meta, {approvedCount} approved.
        </p>
      )}

      <div className="space-y-2">
        {events.map(([key, def]) => {
          const m = mapping[key];
          const isOpen = openKey === key;
          const candidates = (approved || []).filter(t => (def.requiresDocumentHeader ? t.documentHeader : !t.documentHeader));
          const selected = approved && m?.name
            ? approved.find(t => t.name === m.name && (!m.language || t.language === m.language))
            : null;
          const varCount = selected ? selected.variables.length : (m?.params?.length || 0);
          const mismatch = !!selected && (m?.params?.length || 0) !== selected.variables.length;
          const manualNamed = !selected && Array.isArray(m?.paramNames) && m.paramNames.length > 0;
          const varLabel = i => selected?.named ? `{{${selected.variables[i]}}}`
            : manualNamed ? `{{${m.paramNames[i]}}}` : `{{${i + 1}}}`;
          const defaultName = key === "invoice" ? "none — sent as a plain document"
            : def.noDefaultTemplate ? "none — sent as plain text" : key;

          return (
            <div key={key} className={`border rounded-xl ${m?.name ? "border-amber-200 bg-amber-50/30" : "border-stone-200 bg-white"}`}>
              <button type="button" onClick={() => setOpenKey(isOpen ? null : key)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left min-h-[44px]">
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-navy-900">{def.label}</span>
                  <span className="block text-[11px] text-stone-400 truncate">
                    {m?.name ? `→ ${m.name}${m.language ? ` (${m.language})` : ""}` : `Default: ${defaultName}`}
                  </span>
                </span>
                {isOpen ? <ChevronUp size={14} className="text-stone-400 flex-shrink-0" /> : <ChevronDown size={14} className="text-stone-400 flex-shrink-0" />}
              </button>

              {isOpen && (
                <div className="px-4 pb-4 space-y-3">
                  {approved ? (
                    <>
                    <Field label="Template">
                      <select
                        value={selected ? `${selected.name}|${selected.language}` : ""}
                        onChange={e => {
                          const v = e.target.value;
                          if (!v) { pickTemplate(key, def, null); return; }
                          const [n, l] = v.split("|");
                          pickTemplate(key, def, candidates.find(t => t.name === n && t.language === l));
                        }}
                        className={inputClass}>
                        <option value="">{`Use default (${defaultName})`}</option>
                        {candidates.map(t => {
                          const usable = def.requiresDocumentHeader ? t.usableForInvoice : t.usableForMessage;
                          const why = t.status !== "APPROVED" ? ` — ${String(t.status).toLowerCase()}` : t.problems.length ? ` — can't use: ${t.problems.join(", ")}` : "";
                          return (
                            <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`} disabled={!usable}>
                              {`${t.name} (${t.language}) · ${t.variables.length} variable${t.variables.length === 1 ? "" : "s"}${why}`}
                            </option>
                          );
                        })}
                      </select>
                    </Field>
                    <p className="text-[11px] text-stone-400">
                      The language (en or en_US) comes from Meta's approval of that template and has to match it exactly, so it's set automatically.
                    </p>
                    </>
                  ) : (
                    <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Field label="Template name">
                        {/* The placeholder must not read as a saved value: tenants
                            took "invoice_document" for a fixed name. */}
                        <input value={m?.name || ""} className={inputClass}
                          placeholder={key === "invoice" ? "e.g. invoice_document"
                            : def.noDefaultTemplate ? `e.g. ${key}` : `Blank uses "${key}"`}
                          onChange={e => {
                            const name = e.target.value.trim();
                            if (name) setEvent(key, { ...(m || {}), name, params: m?.params || [...def.defaultParams] });
                            // Clearing the name keeps a language typed first, so the
                            // two boxes can be filled in either order.
                            else setEvent(key, m?.language ? { language: m.language } : null);
                          }} />
                      </Field>
                      <Field label="Language">
                        {/* Always editable — it used to stay disabled until a name
                            was typed, which made it look locked. */}
                        <input value={m?.language || ""} className={inputClass}
                          placeholder={`${defaultLanguage} (from Template language code)`}
                          onChange={e => {
                            const language = e.target.value.trim();
                            if (m?.name) setEvent(key, { ...m, language });
                            else setEvent(key, language ? { language } : null);
                          }} />
                      </Field>
                    </div>
                    {m?.language && !m?.name ? (
                      <p className="text-[11px] text-amber-700">Add the template name too — a language is only saved together with a name.</p>
                    ) : (
                      <p className="text-[11px] text-stone-400">Use the language Meta approved for this template — en and en_US are different.</p>
                    )}
                    </>
                  )}

                  {approved && m?.name && !selected && (
                    <p className="text-[11px] text-red-600">
                      “{m.name}”{m.language ? ` (${m.language})` : ""} wasn't found among your Meta templates — pick one above, or use the default.
                    </p>
                  )}
                  {def.requiresDocumentHeader && (
                    <p className="text-[11px] text-stone-400">Must have a <strong>Document</strong> header — the invoice PDF is attached there.</p>
                  )}
                  {selected?.bodyText && (
                    <div className="text-xs text-stone-600 bg-white border border-stone-200 rounded-lg px-3 py-2 whitespace-pre-wrap">
                      {selected.bodyText}
                    </div>
                  )}

                  {m?.name && (
                    <div className="space-y-2">
                      {Array.from({ length: varCount }, (_, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="w-28 flex-shrink-0 font-mono text-xs text-stone-500 truncate">{varLabel(i)}</span>
                          <select value={m.params?.[i] || ""} className={inputClass}
                            onChange={e => {
                              const params = [...(m.params || [])];
                              params[i] = e.target.value;
                              setEvent(key, { ...m, params });
                            }}>
                            {!m.params?.[i] && <option value="">Choose a value…</option>}
                            {def.tokens.map(t => <option key={t} value={t}>{meta.tokenLabels[t] || t}</option>)}
                          </select>
                          {!selected && !manualNamed && (
                            <button type="button" className="text-xs text-stone-400 hover:text-red-600 px-2 min-h-[44px]"
                              onClick={() => setEvent(key, { ...m, params: (m.params || []).filter((_, j) => j !== i) })}>
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                      {!selected && !manualNamed && (
                        <button type="button" className="text-xs font-medium text-amber-600 min-h-[44px]"
                          onClick={() => setEvent(key, { ...m, params: [...(m.params || []), def.tokens[0]] })}>
                          + Add variable
                        </button>
                      )}
                      {varCount === 0 && <p className="text-[11px] text-stone-400">This template has no variables.</p>}
                      {mismatch && (
                        <p className="text-[11px] text-red-600">
                          This template has {selected.variables.length} variable{selected.variables.length === 1 ? "" : "s"} — pick it again to reset, or WhatsApp will reject the message.
                        </p>
                      )}
                      <button type="button" onClick={() => setEvent(key, null)}
                        className="flex items-center gap-1 text-[11px] font-medium text-stone-500 hover:text-red-600">
                        <RotateCcw size={11} /> Use default instead
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      <SaveButton onClick={save} saving={saving} />
    </div>
  );
}

/* ── Telegram — moved here from the super-admin console (2026-09-07). ── */
function TelegramIntegrationSection({ authHeaders, integrations, onSaved }) {
  const tg = integrations.telegram;
  const [botToken, setBotToken] = useState("");
  const [chatId, setChatId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    if (!botToken && !chatId) return;
    setSaving(true); setError("");
    const body = { telegram: { ...(botToken && { botToken }), ...(chatId && { chatId }) } };
    await saveIntegration(authHeaders, body, () => { setBotToken(""); setChatId(""); onSaved(); }, setError);
    setSaving(false);
  }

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-navy-900 uppercase tracking-wider flex items-center gap-2">
        <Send size={15} className="text-blue-500" /> Telegram
        {tg.botTokenSet && <span className="text-emerald-600 normal-case font-normal text-xs">· configured</span>}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Bot Token">
          <input value={botToken} onChange={e => setBotToken(e.target.value)} className={inputClass} placeholder={tg.botTokenSet ? "•••••••• (leave blank to keep)" : "123456:ABC-..."} />
        </Field>
        <Field label="Chat ID">
          <input value={chatId} onChange={e => setChatId(e.target.value)} className={inputClass} placeholder={tg.chatId || "-100123456789"} />
        </Field>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <SaveButton onClick={save} saving={saving} />
    </div>
  );
}

/* ── Email — this property's own SMTP relay (not a shared HostSync one),
   moved here from the super-admin console (2026-09-07). ── */
function EmailIntegrationSection({ authHeaders, integrations, onSaved }) {
  const em = integrations.email;
  const [enabled, setEnabled] = useState(!!em.enabled);
  const [fromName, setFromName] = useState(em.fromName || "");
  const [replyTo, setReplyTo] = useState(em.replyTo || "");
  const [smtpHost, setSmtpHost] = useState(em.smtpHost || "");
  const [smtpPort, setSmtpPort] = useState(em.smtpPort || 587);
  const [smtpUser, setSmtpUser] = useState(em.smtpUser || "");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    setSaving(true); setError("");
    const body = { email: { enabled, fromName, replyTo, smtpHost, smtpPort, smtpUser, ...(smtpPassword && { smtpPassword }) } };
    await saveIntegration(authHeaders, body, () => { setSmtpPassword(""); onSaved(); }, setError);
    setSaving(false);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-navy-900 uppercase tracking-wider flex items-center gap-2">
          <Mail size={15} className="text-amber-600" /> Email
        </h2>
        <button type="button" role="switch" aria-checked={enabled} onClick={() => setEnabled(v => !v)}
          className={`relative inline-flex items-center w-14 h-8 rounded-full shrink-0 transition-colors duration-200 ${enabled ? "bg-amber-500" : "bg-stone-300"}`}>
          <span className={`inline-block w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-200 ${enabled ? "translate-x-7" : "translate-x-1"}`} />
        </button>
      </div>
      <p className="text-xs text-stone-400">
        Sent through your own SMTP relay (your own mailbox/domain) — booking confirmations and
        stay invoices to guests. Won't send until a relay is configured here.
      </p>
      <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-2.5 py-2">
        Prefer port <strong>587</strong> (STARTTLS) over <strong>465</strong> if sending times out —
        our host blocks outbound port 465. 587 works with every provider we've tested (Hostinger, Gmail, Zoho, etc.).
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="SMTP Host"><input value={smtpHost} onChange={e => setSmtpHost(e.target.value)} className={inputClass} placeholder="smtp.gmail.com" /></Field>
        <Field label="SMTP Port"><input type="number" value={smtpPort} onChange={e => setSmtpPort(e.target.value)} className={inputClass} placeholder="587" /></Field>
      </div>
      <Field label="SMTP User"><input value={smtpUser} onChange={e => setSmtpUser(e.target.value)} className={inputClass} placeholder="notifications@yourdomain.com" /></Field>
      <Field label="SMTP Password">
        <input type="password" value={smtpPassword} onChange={e => setSmtpPassword(e.target.value)} className={inputClass}
          placeholder={em.smtpPasswordSet ? "•••••••• (leave blank to keep)" : "App password or SMTP password"} />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="From Name"><input value={fromName} onChange={e => setFromName(e.target.value)} className={inputClass} placeholder="Defaults to your property name" /></Field>
        <Field label="Reply-To"><input value={replyTo} onChange={e => setReplyTo(e.target.value)} className={inputClass} placeholder="Defaults to your own contact email" /></Field>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <SaveButton onClick={save} saving={saving} />
    </div>
  );
}

/* ── Online payments — Razorpay credentials (moved here from the
   super-admin console, 2026-09-07) plus the UPI id/QR (moved here from the
   old standalone "Payment Settings" sidebar link/modal, same
   PATCH /settings/payment route, just relocated) and the pay-later
   toggle (services/paymentGateway.js). ── */
function PaymentsSection({ authHeaders, settings, integrations, onSaved }) {
  const [payLaterEnabled, setPayLaterEnabled] = useState(settings.payments?.payLaterEnabled !== false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const configured = !!settings.payments?.enabled;
  const pay = integrations.payments;
  const [payProvider, setPayProvider] = useState(pay.provider || "none");
  const [payKeyId, setPayKeyId] = useState(pay.keyId || "");
  const [payKeySecret, setPayKeySecret] = useState("");
  const [payWebhookSecret, setPayWebhookSecret] = useState("");
  const [savingRazorpay, setSavingRazorpay] = useState(false);
  const [razorpayError, setRazorpayError] = useState("");

  async function saveRazorpay() {
    setSavingRazorpay(true); setRazorpayError("");
    const body = {
      payments: {
        provider: payProvider, keyId: payKeyId,
        ...(payKeySecret && { keySecret: payKeySecret }),
        ...(payWebhookSecret && { webhookSecret: payWebhookSecret }),
      },
    };
    await saveIntegration(authHeaders, body, () => { setPayKeySecret(""); setPayWebhookSecret(""); onSaved(); }, setRazorpayError);
    setSavingRazorpay(false);
  }

  async function save(next) {
    setPayLaterEnabled(next);
    setSaving(true); setError("");
    try {
      const res = await fetch(`${API}/settings/profile`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ payLaterEnabled: next }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Save failed");
      onSaved();
    } catch (e) { setError(e.message); setPayLaterEnabled(!next); }
    finally { setSaving(false); }
  }

  return (
    <div className="space-y-6">
      <h2 className="text-sm font-semibold text-navy-900 uppercase tracking-wider">Payments</h2>

      <div className="space-y-3">
        <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">
          Razorpay {pay.keySecretSet && <span className="text-emerald-600 normal-case font-normal">· configured</span>}
        </p>
        <p className="text-xs text-stone-400">
          Your own Razorpay account — guests pay their remaining balance online from the guest
          registration link once a booking is approved.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Provider">
            <select value={payProvider} onChange={e => setPayProvider(e.target.value)} className={inputClass}>
              <option value="none">Not set up</option>
              <option value="razorpay">Razorpay</option>
            </select>
          </Field>
          <Field label="Key ID"><input value={payKeyId} onChange={e => setPayKeyId(e.target.value)} className={inputClass} placeholder="rzp_live_..." /></Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Key Secret">
            <input type="password" value={payKeySecret} onChange={e => setPayKeySecret(e.target.value)} className={inputClass}
              placeholder={pay.keySecretSet ? "•••••••• (leave blank to keep)" : "Key secret from Razorpay dashboard"} />
          </Field>
          <Field label="Webhook Secret">
            <input type="password" value={payWebhookSecret} onChange={e => setPayWebhookSecret(e.target.value)} className={inputClass}
              placeholder={pay.webhookSecretSet ? "•••••••• (leave blank to keep)" : "Set the same value in Razorpay's webhook config"} />
          </Field>
        </div>
        <p className="text-[11px] text-stone-400">
          Webhook URL to give Razorpay: <code className="text-stone-500">{window.location.origin}/api/payment/webhook</code>
        </p>
        {razorpayError && <p className="text-sm text-red-600">{razorpayError}</p>}
        <SaveButton onClick={saveRazorpay} saving={savingRazorpay} />
      </div>

      <div className="border-t border-stone-100 pt-6 space-y-3">
        <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Pay Later</p>
        {!configured && (
          <p className="text-sm text-stone-500 bg-stone-50 rounded-xl px-4 py-3">
            Set up Razorpay above first — this setting takes effect once it's configured.
          </p>
        )}
        <div className="flex items-center justify-between gap-4 bg-white border border-stone-200 rounded-xl px-4 py-3">
          <div>
            <p className="text-sm font-medium text-navy-900">Allow guests to pay later</p>
            <p className="text-xs text-stone-400 mt-0.5">
              On: guests may confirm their check-in details without paying online first (pay at
              check-in / via UPI, as today). Off: guests must pay their remaining balance online
              via Razorpay before their check-in details can be submitted.
            </p>
          </div>
          <button type="button" role="switch" aria-checked={payLaterEnabled} disabled={!configured || saving}
            onClick={() => save(!payLaterEnabled)}
            className={`relative inline-flex items-center w-14 h-8 rounded-full shrink-0 transition-colors duration-200 disabled:opacity-40 ${
              payLaterEnabled ? "bg-amber-500" : "bg-stone-300"
            }`}>
            <span className={`inline-block w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
              payLaterEnabled ? "translate-x-7" : "translate-x-1"
            }`} />
          </button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </div>
  );
}

/* ── Property info + About + Location — one form, one PATCH ── */
function ProfileSection({ authHeaders, settings, onSaved }) {
  const [form, setForm] = useState({
    hotelName: settings.hotelName || "", email: settings.email || "",
    phone: settings.phone || "", phone2: settings.phone2 || "", address: settings.address || "",
    aboutText: settings.aboutText || "",
    mapsUrl: settings.mapsUrl || "", mapsLat: settings.mapsLat || "", mapsLng: settings.mapsLng || "",
    googleReviewUrl: settings.googleReviewUrl || "",
    guestPolicyText: settings.guestPolicyText || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    setSaving(true); setError("");
    try {
      const res = await fetch(`${API}/settings/profile`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Save failed");
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-semibold text-navy-900 uppercase tracking-wider">Property Info</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Property Name"><input value={form.hotelName} onChange={e => setForm({ ...form, hotelName: e.target.value })} className={inputClass} /></Field>
        <Field label="Email"><input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputClass} /></Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Phone"><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputClass} placeholder="+91 98765 43210" /></Field>
        <Field label="Phone (secondary)"><input value={form.phone2} onChange={e => setForm({ ...form, phone2: e.target.value })} className={inputClass} /></Field>
      </div>
      <Field label="Address"><input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className={inputClass} /></Field>

      <Field label="About your property" hint="Shown as your 'Our Story' section on the landing page. Leave blank to hide that section.">
        <textarea value={form.aboutText} onChange={e => setForm({ ...form, aboutText: e.target.value })} rows={5} className={inputClass} placeholder="Tell guests what makes your place worth staying at…" />
      </Field>

      <h2 className="text-sm font-semibold text-navy-900 uppercase tracking-wider pt-2 flex items-center gap-1.5">
        <MapPin size={14} /> Location
      </h2>
      <Field label="Google Maps link" hint="In Google Maps: Share → Copy link, then paste it here.">
        <input value={form.mapsUrl} onChange={e => setForm({ ...form, mapsUrl: e.target.value })} className={inputClass} placeholder="https://maps.app.goo.gl/…" />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Latitude (optional)" hint="More precise pin on your map embed."><input value={form.mapsLat} onChange={e => setForm({ ...form, mapsLat: e.target.value })} className={inputClass} placeholder="26.1445" /></Field>
        <Field label="Longitude (optional)"><input value={form.mapsLng} onChange={e => setForm({ ...form, mapsLng: e.target.value })} className={inputClass} placeholder="91.7362" /></Field>
      </div>
      <Field label="Google Review link (optional)" hint="Where the 'Review Us on Google' button sends guests.">
        <input value={form.googleReviewUrl} onChange={e => setForm({ ...form, googleReviewUrl: e.target.value })} className={inputClass} placeholder="https://g.page/r/…" />
      </Field>

      <h2 className="text-sm font-semibold text-navy-900 uppercase tracking-wider pt-2">Guest Policy &amp; Terms</h2>
      <Field label="Guest policy / house rules" hint="Shown to every guest before they can submit a booking or their check-in details, with a mandatory 'I agree' checkbox. Leave blank to skip this entirely — nothing is shown and no agreement is required until you write one.">
        <textarea value={form.guestPolicyText} onChange={e => setForm({ ...form, guestPolicyText: e.target.value })}
          rows={8} className={inputClass}
          placeholder={"e.g.\nGovernment-approved valid photo ID is mandatory for all guests at check-in.\nSmoking is strictly prohibited inside rooms. A cleaning fee will be charged for violations.\nProperty quiet hours: 10:00 PM - 7:00 AM."} />
      </Field>

      {error && <p className="text-sm text-red-600">{error}</p>}
      <SaveButton onClick={save} saving={saving} />
    </div>
  );
}

/* ── Public booking form options — currently just whether ID proof upload
   is offered at all. Some tenants require it at booking time, some ask us
   to hide it (e.g. their own compliance process happens at check-in
   instead) — this is purely a "does the field render" toggle, not a
   required/optional distinction, since the upload was never mandatory
   either way. ── */
function BookingFormSection({ authHeaders, settings, onSaved }) {
  const [values, setValues] = useState({
    idProofUploadEnabled: settings.idProofUploadEnabled !== false,
    sendIdUploadLink:     settings.sendIdUploadLink !== false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function save(field, next) {
    setValues(v => ({ ...v, [field]: next }));
    setSaving(true); setError("");
    try {
      const res = await fetch(`${API}/settings/profile`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: next }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Save failed");
      onSaved();
    } catch (e) { setError(e.message); setValues(v => ({ ...v, [field]: !next })); }
    finally { setSaving(false); }
  }

  // A render helper, not a component: defined inline, a component would be
  // re-created each render and the switch would lose keyboard focus on click.
  const toggle = (field, title, children) => (
    <div className="flex items-center justify-between gap-4 bg-white border border-stone-200 rounded-xl px-4 py-3">
      <div>
        <p className="text-sm font-medium text-navy-900">{title}</p>
        <p className="text-xs text-stone-400 mt-0.5">{children}</p>
      </div>
      <button type="button" role="switch" aria-checked={values[field]} aria-label={title} disabled={saving}
        onClick={() => save(field, !values[field])}
        className={`relative inline-flex items-center w-14 h-8 rounded-full shrink-0 transition-colors duration-200 disabled:opacity-40 ${
          values[field] ? "bg-amber-500" : "bg-stone-300"
        }`}>
        <span className={`inline-block w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
          values[field] ? "translate-x-7" : "translate-x-1"
        }`} />
      </button>
    </div>
  );

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-navy-900 uppercase tracking-wider">Public Booking Form</h2>
      {toggle("idProofUploadEnabled", "Show ID proof upload", <>
        Shows the photo-ID upload (Aadhaar / Passport / Driving Licence) on the booking and
        check-in forms, and makes it <strong>required</strong> — a guest can't submit without
        attaching one. Turn off to hide the field entirely and collect ID in person at
        check-in instead. Guests who already have ID on file aren't asked again.
      </>)}
      {toggle("sendIdUploadLink", "Send ID upload link to OTA guests", <>
        Guests booking through Booking.com, Airbnb, Agoda or MakeMyTrip get a link to upload
        their photo ID in their booking confirmation, and one reminder two days before arrival
        if they haven't. Turn off to collect ID at check-in instead — they still get their
        confirmation, just without the ID request. You can always share the link yourself from
        a booking.
      </>)}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

/* ── In-room menu ordering ──────────────────────────────────────────────
   Lets an in-house guest order from the POS menu themselves. There is no
   separate link to manage here on purpose: the order page reuses the SAME
   per-booking token as the guest-details form, so the link is generated per
   booking from the Bookings page ("Menu link"), and the charge lands on
   that guest's own bill. A single global menu URL would have no way to tell
   whose room to charge. ── */
function GuestOrderingSection({ authHeaders, settings, onSaved, posEnabled }) {
  const [enabled, setEnabled] = useState(settings.guestOrderingEnabled === true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");

  async function save(next) {
    setEnabled(next);
    setSaving(true); setError("");
    try {
      const res = await fetch(`${API}/settings/profile`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ guestOrderingEnabled: next }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Save failed");
      onSaved();
    } catch (e) { setError(e.message); setEnabled(!next); }
    finally { setSaving(false); }
  }

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-navy-900 uppercase tracking-wider">In-Room Ordering</h2>
      <div className="flex items-center justify-between gap-4 bg-white border border-stone-200 rounded-xl px-4 py-3">
        <div>
          <p className="text-sm font-medium text-navy-900">Let guests order from the menu</p>
          <p className="text-xs text-stone-400 mt-0.5">
            {posEnabled
              ? <>Share a guest's <strong>Menu link</strong> from the Bookings page (or print it as a QR for the
                  room). Anything they order is charged to that booking and itemised on their checkout
                  invoice. Only works while the stay is confirmed — a cancelled or checked-out booking
                  stops accepting orders.</>
              : <>Requires the POS module, which isn't enabled for your property. Contact HostSync to turn it on.</>}
          </p>
        </div>
        <button type="button" role="switch" aria-checked={enabled} disabled={saving || !posEnabled}
          onClick={() => save(!enabled)}
          className={`relative inline-flex items-center w-14 h-8 rounded-full shrink-0 transition-colors duration-200 disabled:opacity-40 ${
            enabled && posEnabled ? "bg-amber-500" : "bg-stone-300"
          }`}>
          <span className={`inline-block w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
            enabled && posEnabled ? "translate-x-7" : "translate-x-1"
          }`} />
        </button>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

/* ── Logo ── */
function LogoSection({ authHeaders, settings, onSaved }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  async function upload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("logo", file);
      const res = await fetch(`${API}/settings/logo`, { method: "POST", headers: authHeaders(), body: fd });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Upload failed");
      onSaved();
    } catch (e2) { setError(e2.message); }
    finally { setUploading(false); e.target.value = ""; }
  }

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-navy-900 uppercase tracking-wider">Logo</h2>
      <div className="flex items-center gap-4">
        {settings.logoUrl
          ? <img src={imgSrc(settings.logoUrl)} alt="Logo" className="w-16 h-16 rounded-xl object-cover border border-stone-200" />
          : <div className="w-16 h-16 rounded-xl bg-stone-100 flex items-center justify-center text-stone-300 text-xs">None</div>}
        <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-700 text-sm font-medium hover:bg-stone-200 cursor-pointer">
          <Upload size={14} /> {uploading ? "Uploading…" : "Upload logo"}
          <input type="file" accept="image/*" onChange={upload} disabled={uploading} className="hidden" />
        </label>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

/* ── Landing page photo gallery ── */
function PhotosSection({ authHeaders, settings, onSaved }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  async function upload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true); setError("");
    try {
      // Shrunk first (utils/uploadPhotos.js) — up to 20 full-size phone
      // photos in one request passed nginx's 50MB cap.
      const { ok, message } = splitOversized(await shrinkPhotos(files));
      if (message) setError(message);
      if (!ok.length) return;
      const fd = new FormData();
      ok.forEach(({ file }) => fd.append("photos", file));
      const res = await fetch(`${API}/settings/photos`, { method: "POST", headers: authHeaders(), body: fd });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.error || "Upload failed");
      onSaved();
    } catch (e2) { setError(e2.message); }
    finally { setUploading(false); e.target.value = ""; }
  }

  async function remove(url) {
    try {
      const res = await fetch(`${API}/settings/photos`, {
        method: "DELETE", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Remove failed");
      onSaved();
    } catch (e) { setError(e.message); }
  }

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-navy-900 uppercase tracking-wider flex items-center gap-1.5">
        <ImageIcon size={14} /> Landing Page Photos
      </h2>
      <p className="text-xs text-stone-400">Shown in your public booking page's gallery. Add a few of your best shots. Up to {PHOTO_MAX_MB}MB per photo.</p>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {(settings.heroImages || []).map(url => (
          <div key={url} className="relative group">
            <img src={imgSrc(url)} alt="" className="w-full h-24 object-cover rounded-lg border border-stone-200" />
            <button onClick={() => remove(url)}
              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-navy-950/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
      <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-700 text-sm font-medium hover:bg-stone-200 cursor-pointer">
        <Upload size={14} /> {uploading ? "Uploading…" : "Add photos"}
        <input type="file" accept="image/*" multiple onChange={upload} disabled={uploading} className="hidden" />
      </label>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

/* ── FAQ — full CRUD (create/update/delete/reorder/hide), replacing the
   hardcoded default questions LandingPage.jsx used to show every tenant. ── */
function FaqSection({ authHeaders, onSaved }) {
  const [faqs, setFaqs] = useState(null);
  const [editingId, setEditingId] = useState(null); // null = not editing; "new" = the add form
  const [draft, setDraft] = useState({ question: "", answer: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  function load() {
    fetch(`${API}/faqs`, { headers: authHeaders() }).then(r => r.json()).then(j => {
      if (j.success) setFaqs(j.data);
    }).catch(() => {});
  }
  useEffect(load, []);

  function startAdd() { setEditingId("new"); setDraft({ question: "", answer: "" }); setError(""); }
  function startEdit(f) { setEditingId(f._id); setDraft({ question: f.question, answer: f.answer }); setError(""); }
  function cancelEdit() { setEditingId(null); setError(""); }

  async function saveDraft() {
    if (!draft.question.trim() || !draft.answer.trim()) { setError("Both question and answer are required."); return; }
    setSaving(true); setError("");
    try {
      const isNew = editingId === "new";
      const res = await fetch(`${API}/faqs${isNew ? "" : `/${editingId}`}`, {
        method: isNew ? "POST" : "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(isNew ? { ...draft, order: (faqs?.length || 0) } : draft),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Save failed");
      setEditingId(null);
      load(); onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      const res = await fetch(`${API}/faqs/${id}`, { method: "DELETE", headers: authHeaders() });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Delete failed");
      load(); onSaved();
    } catch (e) { setError(e.message); }
  }

  async function toggleActive(f) {
    try {
      const res = await fetch(`${API}/faqs/${f._id}`, {
        method: "PATCH", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ active: !f.active }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Update failed");
      load(); onSaved();
    } catch (e) { setError(e.message); }
  }

  async function move(index, direction) {
    if (!faqs) return;
    const target = index + direction;
    if (target < 0 || target >= faqs.length) return;
    const a = faqs[index], b = faqs[target];
    try {
      await Promise.all([
        fetch(`${API}/faqs/${a._id}`, { method: "PATCH", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify({ order: b.order }) }),
        fetch(`${API}/faqs/${b._id}`, { method: "PATCH", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify({ order: a.order }) }),
      ]);
      load(); onSaved();
    } catch { /* best-effort reorder — a failed swap just leaves the old order */ }
  }

  if (!faqs) return null;

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-navy-900 uppercase tracking-wider flex items-center gap-1.5">
        <HelpCircle size={14} /> Frequently Asked Questions
      </h2>
      <p className="text-xs text-stone-400">Shown on your public booking page. Hidden entirely until you add at least one.</p>

      <div className="space-y-2">
        {faqs.map((f, i) => (
          <div key={f._id} className={`border rounded-xl p-4 ${f.active ? "border-stone-200 bg-white" : "border-stone-100 bg-stone-50 opacity-60"}`}>
            {editingId === f._id ? (
              <div className="space-y-2">
                <input value={draft.question} onChange={e => setDraft({ ...draft, question: e.target.value })} className={inputClass} placeholder="Question" />
                <textarea value={draft.answer} onChange={e => setDraft({ ...draft, answer: e.target.value })} rows={3} className={inputClass} placeholder="Answer" />
                {error && <p className="text-xs text-red-600">{error}</p>}
                <div className="flex gap-2">
                  <button onClick={saveDraft} disabled={saving} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-navy-900 text-white text-xs font-semibold hover:bg-navy-800 disabled:opacity-60">
                    <Check size={13} /> {saving ? "Saving…" : "Save"}
                  </button>
                  <button onClick={cancelEdit} className="px-3 py-2 rounded-lg bg-stone-100 text-stone-600 text-xs font-medium hover:bg-stone-200">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-navy-900 text-sm">{f.question}</p>
                  <p className="text-sm text-stone-500 mt-1">{f.answer}</p>
                  {!f.active && <p className="text-[11px] text-stone-400 mt-1">Hidden from your booking page</p>}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 text-stone-400 hover:text-navy-700 disabled:opacity-30"><ChevronUp size={14} /></button>
                  <button onClick={() => move(i, 1)} disabled={i === faqs.length - 1} className="p-1.5 text-stone-400 hover:text-navy-700 disabled:opacity-30"><ChevronDown size={14} /></button>
                  <button onClick={() => toggleActive(f)} className="p-1.5 text-stone-400 hover:text-navy-700" title={f.active ? "Hide" : "Show"}>
                    {f.active ? <X size={14} /> : <Check size={14} />}
                  </button>
                  <button onClick={() => startEdit(f)} className="p-1.5 text-stone-400 hover:text-navy-700"><Edit2 size={14} /></button>
                  <button onClick={() => remove(f._id)} className="p-1.5 text-stone-400 hover:text-red-600"><Trash2 size={14} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {editingId === "new" ? (
        <div className="border border-amber-300 rounded-xl p-4 space-y-2">
          <input value={draft.question} onChange={e => setDraft({ ...draft, question: e.target.value })} className={inputClass} placeholder="Question" autoFocus />
          <textarea value={draft.answer} onChange={e => setDraft({ ...draft, answer: e.target.value })} rows={3} className={inputClass} placeholder="Answer" />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button onClick={saveDraft} disabled={saving} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-navy-900 text-white text-xs font-semibold hover:bg-navy-800 disabled:opacity-60">
              <Check size={13} /> {saving ? "Saving…" : "Add"}
            </button>
            <button onClick={cancelEdit} className="px-3 py-2 rounded-lg bg-stone-100 text-stone-600 text-xs font-medium hover:bg-stone-200">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={startAdd} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-700 text-sm font-medium hover:bg-stone-200">
          <Plus size={14} /> Add question
        </button>
      )}
    </div>
  );
}

/* ── Custom booking-form fields — same shape as FaqSection above (an
   inline add/edit/delete/reorder/hide-toggle list backed by the same
   GET-all/POST/PATCH-whitelist/DELETE route pattern). Lets a tenant add
   whatever extra question their own property needs (arrival time, special
   dietary needs, an internal reference number, ...) without a code change
   or a redeploy — CLAUDE.md I5. ── */
const EMPTY_DRAFT = { label: "", inputType: "text", options: "", required: false };

function CustomFieldsSection({ authHeaders, onSaved }) {
  const [fields, setFields] = useState(null);
  const [editingId, setEditingId] = useState(null); // null = not editing; "new" = the add form
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  function load() {
    fetch(`${API}/custom-fields`, { headers: authHeaders() }).then(r => r.json()).then(j => {
      if (j.success) setFields(j.data);
    }).catch(() => {});
  }
  useEffect(load, []);

  function startAdd() { setEditingId("new"); setDraft(EMPTY_DRAFT); setError(""); }
  function startEdit(f) {
    setEditingId(f._id);
    // inputType isn't editable after creation (changing it would silently
    // reinterpret every answer already collected under the old shape) —
    // only pass along what the PATCH route actually accepts.
    setDraft({ label: f.label, inputType: f.inputType, options: (f.options || []).join(", "), required: f.required });
    setError("");
  }
  function cancelEdit() { setEditingId(null); setError(""); }

  async function saveDraft() {
    if (!draft.label.trim()) { setError("A field label is required."); return; }
    setSaving(true); setError("");
    try {
      const isNew = editingId === "new";
      const optionsList = draft.options.split(",").map(s => s.trim()).filter(Boolean);
      const body = isNew
        ? { label: draft.label, inputType: draft.inputType, options: optionsList, required: draft.required, order: (fields?.length || 0) }
        : { label: draft.label, options: optionsList, required: draft.required }; // inputType omitted — not editable
      const res = await fetch(`${API}/custom-fields${isNew ? "" : `/${editingId}`}`, {
        method: isNew ? "POST" : "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Save failed");
      setEditingId(null);
      load(); onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this field? Any answers guests already gave for it stay on their booking, but the field itself won't ask again.")) return;
    try {
      const res = await fetch(`${API}/custom-fields/${id}`, { method: "DELETE", headers: authHeaders() });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Delete failed");
      load(); onSaved();
    } catch (e) { setError(e.message); }
  }

  async function toggleActive(f) {
    try {
      const res = await fetch(`${API}/custom-fields/${f._id}`, {
        method: "PATCH", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ active: !f.active }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Update failed");
      load(); onSaved();
    } catch (e) { setError(e.message); }
  }

  async function move(index, direction) {
    if (!fields) return;
    const target = index + direction;
    if (target < 0 || target >= fields.length) return;
    const a = fields[index], b = fields[target];
    try {
      await Promise.all([
        fetch(`${API}/custom-fields/${a._id}`, { method: "PATCH", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify({ order: b.order }) }),
        fetch(`${API}/custom-fields/${b._id}`, { method: "PATCH", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify({ order: a.order }) }),
      ]);
      load(); onSaved();
    } catch { /* best-effort reorder — a failed swap just leaves the old order */ }
  }

  if (!fields) return null;

  const typeLabel = { text: "Short text", textarea: "Long text", number: "Number", select: "Dropdown", checkbox: "Yes/No" };

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-navy-900 uppercase tracking-wider flex items-center gap-1.5">
        <ListPlus size={14} /> Custom Booking Form Fields
      </h2>
      <p className="text-xs text-stone-400">Extra questions shown on your public booking form, in the order below. Hidden entirely until you add at least one.</p>

      <div className="space-y-2">
        {fields.map((f, i) => (
          <div key={f._id} className={`border rounded-xl p-4 ${f.active ? "border-stone-200 bg-white" : "border-stone-100 bg-stone-50 opacity-60"}`}>
            {editingId === f._id ? (
              <div className="space-y-2">
                <input value={draft.label} onChange={e => setDraft({ ...draft, label: e.target.value })} className={inputClass} placeholder="Field label" />
                {draft.inputType === "select" && (
                  <input value={draft.options} onChange={e => setDraft({ ...draft, options: e.target.value })} className={inputClass} placeholder="Comma-separated options" />
                )}
                <label className="flex items-center gap-2 text-xs text-stone-600">
                  <input type="checkbox" checked={draft.required} onChange={e => setDraft({ ...draft, required: e.target.checked })} />
                  Required
                </label>
                {error && <p className="text-xs text-red-600">{error}</p>}
                <div className="flex gap-2">
                  <button onClick={saveDraft} disabled={saving} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-navy-900 text-white text-xs font-semibold hover:bg-navy-800 disabled:opacity-60">
                    <Check size={13} /> {saving ? "Saving…" : "Save"}
                  </button>
                  <button onClick={cancelEdit} className="px-3 py-2 rounded-lg bg-stone-100 text-stone-600 text-xs font-medium hover:bg-stone-200">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-navy-900 text-sm">{f.label} {f.required && <span className="text-red-500">*</span>}</p>
                  <p className="text-xs text-stone-400 mt-0.5">
                    {typeLabel[f.inputType] || f.inputType}
                    {f.inputType === "select" && f.options?.length ? ` — ${f.options.join(", ")}` : ""}
                  </p>
                  {!f.active && <p className="text-[11px] text-stone-400 mt-1">Hidden from your booking page</p>}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 text-stone-400 hover:text-navy-700 disabled:opacity-30"><ChevronUp size={14} /></button>
                  <button onClick={() => move(i, 1)} disabled={i === fields.length - 1} className="p-1.5 text-stone-400 hover:text-navy-700 disabled:opacity-30"><ChevronDown size={14} /></button>
                  <button onClick={() => toggleActive(f)} className="p-1.5 text-stone-400 hover:text-navy-700" title={f.active ? "Hide" : "Show"}>
                    {f.active ? <X size={14} /> : <Check size={14} />}
                  </button>
                  <button onClick={() => startEdit(f)} className="p-1.5 text-stone-400 hover:text-navy-700"><Edit2 size={14} /></button>
                  <button onClick={() => remove(f._id)} className="p-1.5 text-stone-400 hover:text-red-600"><Trash2 size={14} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {editingId === "new" ? (
        <div className="border border-amber-300 rounded-xl p-4 space-y-2">
          <input value={draft.label} onChange={e => setDraft({ ...draft, label: e.target.value })} className={inputClass} placeholder="Field label — e.g. Expected arrival time" autoFocus />
          <select value={draft.inputType} onChange={e => setDraft({ ...draft, inputType: e.target.value })} className={inputClass}>
            <option value="text">Short text</option>
            <option value="textarea">Long text</option>
            <option value="number">Number</option>
            <option value="select">Dropdown</option>
            <option value="checkbox">Yes/No</option>
          </select>
          {draft.inputType === "select" && (
            <input value={draft.options} onChange={e => setDraft({ ...draft, options: e.target.value })} className={inputClass} placeholder="Comma-separated options" />
          )}
          <label className="flex items-center gap-2 text-xs text-stone-600">
            <input type="checkbox" checked={draft.required} onChange={e => setDraft({ ...draft, required: e.target.checked })} />
            Required — guest can't submit the form without answering
          </label>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button onClick={saveDraft} disabled={saving} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-navy-900 text-white text-xs font-semibold hover:bg-navy-800 disabled:opacity-60">
              <Check size={13} /> {saving ? "Saving…" : "Add"}
            </button>
            <button onClick={cancelEdit} className="px-3 py-2 rounded-lg bg-stone-100 text-stone-600 text-xs font-medium hover:bg-stone-200">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={startAdd} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-700 text-sm font-medium hover:bg-stone-200">
          <Plus size={14} /> Add field
        </button>
      )}
    </div>
  );
}

/* ── WhatsApp message wording — one textarea per guest-facing event.
   Blank textarea === "use our default wording" (services/whatsappService.js
   treats an absent/empty override identically to none set at all), so
   "Reset to default" is just clearing the field, not a separate concept. ── */
const MESSAGE_EVENTS = [
  { key: "booking_received", label: "Booking request received", tokens: ["name", "hotelName", "ref", "checkIn", "checkOut", "nights", "guests", "contact"] },
  { key: "booking_confirmed", label: "Booking confirmed", tokens: ["name", "hotelName", "ref", "checkIn", "checkOut", "nights", "guests", "total", "advance", "balance", "address", "contact"] },
  { key: "booking_rejected", label: "Booking rejected", tokens: ["name", "hotelName", "ref", "checkIn", "checkOut", "reason", "contact"] },
  { key: "booking_cancelled", label: "Confirmed booking cancelled", tokens: ["name", "hotelName", "ref", "checkIn", "checkOut", "reason", "contact"] },
  { key: "checkin_reminder", label: "Check-in reminder (day before)", tokens: ["name", "hotelName", "ref", "checkIn", "guests", "address", "contact"] },
  { key: "checkout_thankyou", label: "Checkout thank-you + invoice", tokens: ["name", "hotelName", "ref", "reviewUrl", "invoiceUrl", "contact"] },
  { key: "checkout_balance_due", label: "Balance due at checkout", tokens: ["name", "hotelName", "ref", "balance", "contact"] },
  { key: "payment_update", label: "Payment status update", tokens: ["name", "hotelName", "ref", "total", "advance", "balance", "contact"] },
];

function MessageTemplatesSection({ authHeaders, onSaved }) {
  const [drafts, setDrafts] = useState(null); // { [eventKey]: text }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => {
    fetch(`${API}/settings/message-templates`, { headers: authHeaders() }).then(r => r.json()).then(j => {
      if (j.success) setDrafts(Object.fromEntries(MESSAGE_EVENTS.map(e => [e.key, j.data[e.key] || ""])));
    }).catch(() => setDrafts(Object.fromEntries(MESSAGE_EVENTS.map(e => [e.key, ""]))));
  }, []);

  async function save() {
    setSaving(true); setError("");
    try {
      const res = await fetch(`${API}/settings/message-templates`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ templates: drafts }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Save failed");
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  if (!drafts) return null;
  const customizedCount = MESSAGE_EVENTS.filter(e => drafts[e.key]?.trim()).length;

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-navy-900 uppercase tracking-wider flex items-center gap-1.5">
        <MessageCircle size={14} /> WhatsApp Message Wording
      </h2>
      <p className="text-xs text-stone-400">
        Rewrite what guests receive for each event, in your own voice. Leave any of these blank to use our
        default wording — {customizedCount === 0 ? "you haven't customized any yet" : `${customizedCount} of ${MESSAGE_EVENTS.length} customized`}.
      </p>

      <div className="space-y-2">
        {MESSAGE_EVENTS.map(e => {
          const isOpen = openKey === e.key;
          const isCustom = !!drafts[e.key]?.trim();
          return (
            <div key={e.key} className={`border rounded-xl ${isCustom ? "border-amber-200 bg-amber-50/30" : "border-stone-200 bg-white"}`}>
              <button onClick={() => setOpenKey(isOpen ? null : e.key)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left">
                <span className="text-sm font-medium text-navy-900">
                  {e.label} {isCustom && <span className="text-[11px] font-normal text-amber-600 ml-1">Custom</span>}
                </span>
                {isOpen ? <ChevronUp size={14} className="text-stone-400 flex-shrink-0" /> : <ChevronDown size={14} className="text-stone-400 flex-shrink-0" />}
              </button>
              {isOpen && (
                <div className="px-4 pb-4 space-y-2">
                  <textarea
                    value={drafts[e.key]}
                    onChange={ev => setDrafts(d => ({ ...d, [e.key]: ev.target.value }))}
                    rows={5}
                    className={inputClass + " font-mono text-xs"}
                    placeholder="Leave blank to use our default wording…"
                  />
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] text-stone-400">
                      Available: {e.tokens.map(t => `{{${t}}}`).join("  ")}
                    </p>
                    {isCustom && (
                      <button onClick={() => setDrafts(d => ({ ...d, [e.key]: "" }))}
                        className="flex items-center gap-1 text-[11px] font-medium text-stone-500 hover:text-red-600 flex-shrink-0">
                        <RotateCcw size={11} /> Reset to default
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      <SaveButton onClick={save} saving={saving} />
    </div>
  );
}
